import { ApiRoute } from '@routes/apiRoutes';
import { httpService } from '@services/httpService';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@enums/queryKeys';
import { toast } from 'react-hot-toast';
import { downloadService } from '@services/downloadService';
import {
  AttachmentModel,
  AttachmentRequestModel,
  AttachmentUpdateModel,
} from '@models/attachments';
import { AttachmentSearchByDrawRequestModel } from '@models/attachments/AttachmentSearchByDrawRequestModel';
import { AttachmentDeleteByIdRequestModel } from '@models/attachments/AttachmentDeleteByIdRequestModel';

export const useAttachmentApi = {
  useGetAttachmentsByDrawRequest: (
    drawRequestRecId: string,
    sourceRecId: string,
    enabled: boolean = true
  ) => {
    return useQuery({
      queryKey: [QueryKeys.ATTACHMENTS, drawRequestRecId, sourceRecId],
      queryFn: () => {
        const request: AttachmentSearchByDrawRequestModel = {
          DrawRequestRecId: drawRequestRecId,
          SourceRecId: sourceRecId,
        };
        return httpService.post<AttachmentModel[]>(
          ApiRoute.AttachmentList,
          request
        );
      },
      enabled: enabled && !!drawRequestRecId && !!sourceRecId,
      staleTime: 30000,
      gcTime: 300000,
    });
  },

  useAddAttachment: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (attachment: AttachmentRequestModel) => {
        return httpService.post<{ recId: string }>(
          ApiRoute.AttachmentAdd,
          attachment
        );
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeys.ATTACHMENTS,
            variables.DrawRequestRecId,
            variables.SourceRecId,
          ],
        });
        toast.success('File uploaded successfully');
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to upload file');
      },
    });
  },

  useUpdateAttachment: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (updateData: {
        attachment: AttachmentUpdateModel;
        drawRequestRecId: string;
        sourceRecId: string;
      }) => {
        const request = {
          ...updateData.attachment,
          SourceRecId: updateData.sourceRecId,
        };
        return httpService.post<{ success: boolean }>(
          ApiRoute.AttachmentUpdate,
          request
        );
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeys.ATTACHMENTS,
            variables.drawRequestRecId,
            variables.sourceRecId,
          ],
        });
        toast.success('Attachment updated successfully');
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to update attachment');
      },
    });
  },

  useDeleteAttachment: () => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: async (deleteData: {
        recId: string;
        drawRequestRecId: string;
        sourceRecId: string;
      }) => {
        const request: AttachmentDeleteByIdRequestModel = {
          RecId: deleteData.recId,
          SourceRecId: deleteData.sourceRecId,
        };
        return httpService.post<{ success: boolean }>(
          ApiRoute.AttachmentDelete,
          request
        );
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          queryKey: [
            QueryKeys.ATTACHMENTS,
            variables.drawRequestRecId,
            variables.sourceRecId,
          ],
        });
        toast.success('Attachment deleted successfully');
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to delete attachment');
      },
    });
  },

  downloadAttachment: async (
    attachmentRecId: string,
    sourceRecId: string,
    fileName?: string
  ): Promise<void> => {
    try {
      await downloadService.downloadDocument(
        attachmentRecId,
        sourceRecId,
        fileName
      );
    } catch (error) {
      console.error('Error downloading attachment:', error);
      throw error;
    }
  },
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
};
